/**
 * Relación entre los documentos de archivo de un embarque y el objeto que cada
 * uno tiene en Storage.
 *
 * No es una relación uno a uno: la ruta se arma con el nombre del archivo, así
 * que subir dos veces el mismo nombre al mismo embarque sobrescribe el objeto y
 * deja **dos documentos apuntando a uno solo**. Borrar cualquiera de los dos
 * borraba el objeto y dejaba al otro señalando a la nada —la factura del
 * embarque Zs7dmoL9yZB7sTrJHWEK de Partequipos, 23-sep-2026—.
 */

/** Lo que hace falta de un documento de archivo para ubicar su objeto. */
export interface ShipmentFileRef {
  id?: string
  storagePath?: string
  fileName?: string
}

/**
 * Ruta del objeto en Storage. `storagePath` manda cuando existe; hoy no lo
 * guarda ningún archivo de embarque, así que en la práctica siempre se arma con
 * el nombre, igual que al subir.
 */
export const shipmentFileStoragePath = (
  file: ShipmentFileRef,
  companyId: string,
  shipmentId: string
): string =>
  file.storagePath?.trim()
    ? file.storagePath
    : `${companyId}/shipmentFiles/${shipmentId}/${file.fileName ?? ''}`

/**
 * Si algún otro documento del embarque resuelve al mismo objeto. Cuando lo hay,
 * el objeto no se puede borrar: todavía tiene quién lo use.
 *
 * `others` no debe incluir el documento que se está borrando.
 */
export const storagePathIsShared = (
  others: ShipmentFileRef[],
  path: string,
  companyId: string,
  shipmentId: string
): boolean =>
  others.some(other => {
    // Un documento sin nombre ni ruta no apunta a nada; contarlo bloquearía el
    // borrado del objeto para siempre.
    if (!other.storagePath?.trim() && !other.fileName?.trim()) return false
    return shipmentFileStoragePath(other, companyId, shipmentId) === path
  })
